import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdOutlineCall } from "react-icons/md";
import { getCallPeerSnapshot } from "../../../utils/callPeerSnapshot";
import TextTranslate from "../../../utils/TextTranslate";

function formatElapsed(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

type Props = {
  /** In Home column (above MessageHeader) vs fixed fallback */
  variant?: "embedded" | "floating";
};

/** Slim green shimmer strip — return to active call. Title is frozen for the call. */
export default function ReturnToCallBar({ variant = "embedded" }: Props) {
  const navigate = useNavigate();
  const callType = sessionStorage.getItem("call_type");
  const [elapsed, setElapsed] = useState(0);
  const [hasTimer, setHasTimer] = useState(false);
  const [title, setTitle] = useState(() => {
    const stored = getCallPeerSnapshot().title;
    if (stored) return stored;
    return callType === "audio" ? "Audio call" : "Video call";
  });

  useEffect(() => {
    const stored = getCallPeerSnapshot().title;
    if (stored) setTitle(stored);
  }, []);

  useEffect(() => {
    const tick = () => {
      const start = sessionStorage.getItem("callStartTime");
      if (!start) {
        setHasTimer(false);
        return;
      }
      setHasTimer(true);
      setElapsed(Math.floor((Date.now() - Number(start)) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  const shell =
    variant === "embedded"
      ? "relative z-40 w-full shrink-0"
      : "fixed inset-x-0 top-0 z-[95] w-full pt-[env(safe-area-inset-top)]";

  return (
    <button
      type="button"
      onClick={() => navigate("/video-call")}
      className={`${shell} group return-to-call-strip`}
      aria-label="Return to call"
    >
      <span className="return-to-call-strip__glow" aria-hidden />
      <span className="return-to-call-strip__noise" aria-hidden />
      <span className="relative flex h-10 w-full items-center gap-2.5 px-3 text-left text-white sm:h-11 sm:gap-3 sm:px-5 lg:px-10">
        <span className="relative grid h-7 w-7 shrink-0 place-content-center rounded-full bg-white/20 ring-1 ring-white/30 backdrop-blur-sm sm:h-8 sm:w-8">
          <span className="absolute inset-0 animate-ping rounded-full bg-white/25" />
          <MdOutlineCall className="relative text-base sm:text-lg" />
        </span>
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-[13px] font-semibold tracking-wide sm:text-[14px]">
            {title}
          </span>
          <span className="block truncate text-[11px] text-white/85 sm:text-[12px]">
            {hasTimer ? (
              <span className="tabular-nums">{formatElapsed(elapsed)}</span>
            ) : (
              <TextTranslate text="Call in progress" />
            )}
            <span className="mx-1 opacity-60">·</span>
            <TextTranslate text="Tap to return" />
          </span>
        </span>
        <span className="shrink-0 rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-white ring-1 ring-white/35 backdrop-blur-sm transition group-hover:bg-white/30 sm:px-3 sm:text-[12px]">
          <TextTranslate text="Return" />
        </span>
      </span>
    </button>
  );
}
